import React, { useState, useMemo } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
interface RequestBoxPopupProps {
  isOpen: boolean;
  onClose: () => void;
  fullStats: any[];
  onRefresh?: () => Promise<void>
  // ✅ 1. เพิ่ม Prop เพื่อรับรายชื่อหุ้นฝั่งแอดมินเข้ามาเปรียบเทียบตั๋วตรวจ
}
const formatThaiDate = (isoString: string) => {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '-';
  }
};
const RequestBoxPopup: React.FC<RequestBoxPopupProps> = ({ isOpen, onClose, fullStats, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // ⚙️ 2. ขยายขีดความสามารถการ Sort: เพิ่มประเภท 'adminOnly' เข้าไปในเงื่อนไข
  const [sortBy, setSortBy] = useState<'count' | 'alphabet' | 'lastRequest' | 'adminOnly'>('lastRequest');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // ⚙️ เพิ่ม States สำหรับระบบเลือกหุ้น (Selection Mode)
  const [isSelectMode, setIsSelectMode] = useState<boolean>(false);
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
const { adminPortfolios } = usePortfolio();

  // 🔄 3. รื้อไพ่ Logic การคัดกรองใน useMemo
  const filteredStats = useMemo(() => {
    let result = [...fullStats];

    // 1. กรองคำค้นหาปกติ (รองรับ Safe Checking เผื่อเจอค่าว่าง)
    if (searchTerm) {
      result = result.filter(item =>
        item?.ticker && String(item.ticker).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 2. 🔥 ด่านคัดกรองใหม่: แกะก้อน Portfolios เพื่อหาหุ้นที่ User ถืออยู่จริง ๆ
    if (sortBy === 'adminOnly') {
      const myPortfolioTickers = new Set<string>();

      // ✅ 3. วนลูปแกะข้อมูลจาก adminPortfolios (รองรับทั้ง V1: stocks และ V2: holdings)
      if (Array.isArray(adminPortfolios)) {
        adminPortfolios.forEach((p: any) => {
          p?.subPorts?.forEach((sp: any) => {
            const items = sp.holdings || sp.stocks || [];
            items.forEach((s: any) => {
              const sym = s.symbol || s.assetSymbol || s.stockSymbol;
              if (sym) {
                myPortfolioTickers.add(String(sym).toLowerCase());
              }
            });
          });
        });
      }
      
      // กรองเก็บไว้เฉพาะคำขอจากทางบ้านที่มีรายชื่ออยู่ในพอร์ตของแอดมิน/ผู้ใช้จริง ๆ
      result = result.filter(item => 
        item?.ticker && myPortfolioTickers.has(String(item.ticker).toLowerCase())
      );
    }

    // 3. เรียงลำดับคิวข้อมูลตามเงื่อนไข (Sort Logic)
    return result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'count') {
        comparison = (b.count || 0) - (a.count || 0);
      } else if (sortBy === 'alphabet') {
        const tickerA = a?.ticker || '';
        const tickerB = b?.ticker || '';
        comparison = tickerA.localeCompare(tickerB);
      } else if (sortBy === 'lastRequest' || sortBy === 'adminOnly') {
        const timeA = a?.lastRequest ? new Date(a.lastRequest).getTime() : 0;
        const timeB = b?.lastRequest ? new Date(b.lastRequest).getTime() : 0;
        comparison = timeB - timeA;
      }
      return sortDirection === 'desc' ? comparison : -comparison;
    });
  }, [fullStats, searchTerm, sortBy, sortDirection, adminPortfolios]);

  const handleOpenAll = () => {
    filteredStats.forEach((item: any) => {
      window.open(`/?symbol=${item.ticker}`, '_blank');
    });
  };
  const handleToggleSelectTicker = (ticker: string) => {
    setSelectedTickers(prev =>
      prev.includes(ticker) ? prev.filter(t => t !== ticker) : [...prev, ticker]
    );
  };

  // 🔥 โกงบราวเซอร์แอบเปิดแท็บเงียบๆ ไว้ข้างหลัง (Background Tab)
  const openUrlInBackground = (ticker: string) => {
    const url = `/?symbol=${ticker}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    const mouseEvent = new MouseEvent('click', {
      ctrlKey: true,
      metaKey: true
    });
    a.dispatchEvent(mouseEvent);
  };
  // 🚀 สั่งเปิดแท็บทั้งหมดตามลิสต์ที่เลือกไว้
  const handleOpenSelected = () => {
    if (selectedTickers.length === 0) return;

    selectedTickers.forEach((ticker) => {
      openUrlInBackground(ticker);
    });

    // เปิดเสร็จแล้วเคลียร์ตัวเลือกเพื่อให้พร้อมสำหรับรอบถัดไป
    setSelectedTickers([]);
    setIsSelectMode(false);
  };

  // 🔀 ฟังก์ชันกดยกเลิก/เปิดโหมดเลือก
  const toggleSelectMode = () => {
    if (isSelectMode) {
      setSelectedTickers([]); // กดยกเลิกโหมดให้เคลียร์อาร์เรย์ทิ้งด้วย
    }
    setIsSelectMode(!isSelectMode);
  };
// ✅ 3. ฟังก์ชันจัดการเมื่อกดปุ่ม Refresh
  const handleRefreshClick = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh(); // สั่งหน้าแม่ยิง API ดึงประวัติคำขอชุดใหม่
    } catch (error) {
      console.error("Refresh statistics error:", error);
    } finally {
      // หน่วงเวลาอนิเมชันนิดนึงให้ User เห็นว่าระบบทำงานแล้ว
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[#1c1c1e] border border-gray-800 w-full max-w-3xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-black/10">
          <h3 className="text-lg font-black text-white flex items-center gap-2">
            <span className="text-red-500">🔥</span> Request จากทางบ้าน ({fullStats.length})
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">✕</button>
        </div>

        {/* Control Panel (Search & Sort) */}
        <div className="p-4 bg-black/20 border-b border-gray-800 flex flex-col md:flex-row gap-3">
          {/* ช่องค้นหา */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="ค้นหาชื่อหุ้น..."
              value={searchTerm.toUpperCase()}
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-yellow-500/50 transition-all"
            />
          </div>

          {/* แผงจัดการคิวเรียงข้อมูล */}
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e: any) => setSortBy(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-300 focus:outline-none cursor-pointer"
            >
              <option value="lastRequest">⏰ ขอล่าสุด</option>
              <option value="adminOnly">👨‍💻 ของแอดมิน </option>
              <option value="count">📊 จำนวน Request</option>
              <option value="alphabet">🔤 ชื่อหุ้น A-Z</option>
            </select>

            <button
              onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="bg-gray-900 border border-gray-700 hover:border-gray-500 text-gray-300 rounded-xl px-3 py-2 text-xs transition-all font-medium min-w-[85px]"
            >
              {sortDirection === 'desc' ? '⬇️ มาก ➡️ น้อย' : '⬆️ น้อย ➡️ มาก'}
            </button>
          </div>
        </div>

        {/* List Content (3 Columns) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 py-6 px-10 overflow-y-auto max-h-[60vh] w-full auto-rows-max justify-start items-start">
          {filteredStats.map((item: any) => {
            const isChecked = selectedTickers.includes(item.ticker);

            // 💡 แผนรับมือเด็ด: ถ้าเปิดโหมดเลือก ให้ใช้แท็ก <div> เพื่อไม่ให้บราวเซอร์บังคับเปิดแท็บใหม่
            // แต่ถ้าไม่ได้เลือก ให้ใช้ <a> ชาร์จพลังโกง Background Tab เพื่อ UX ที่ลื่นไหลตามปกติ
            const Component = isSelectMode ? 'div' : 'a';
            
            const extraProps = isSelectMode 
              ? {
                  onClick: () => handleToggleSelectTicker(item.ticker)
                }
              : {
                  href: `/?symbol=${item.ticker}`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  onClick: (e: React.MouseEvent) => {
                    if (!e.ctrlKey && !e.metaKey) {
                      const clonedEvent = new MouseEvent('click', { ctrlKey: true, metaKey: true });
                      e.currentTarget.dispatchEvent(clonedEvent);
                      e.preventDefault();
                    }
                  }
                };

            return (
              <Component
                key={item.ticker}
                {...extraProps}
                className={`flex flex-col p-4 border rounded-xl transition-all cursor-pointer group min-w-[150px] w-full h-auto no-underline text-current ${
                  isChecked 
                    ? 'bg-yellow-500/10 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)]' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-yellow-500/30'
                }`}
              >
                <div className="flex justify-between items-start w-full mb-2">
                  <div className="flex items-center gap-2">
                    {/* ✅ เพิ่มกล่อง Checkbox แสดงสถานะการเลือกโชว์ให้เห็นชัดเจน */}
                    {isSelectMode && (
                      <div className={`w-4 h-4 rounded border text-[10px] flex items-center justify-center transition-all ${
                        isChecked ? 'bg-yellow-500 border-yellow-500 text-black font-black' : 'border-gray-600 bg-black/40'
                      }`}>
                        {isChecked && '✓'}
                      </div>
                    )}
                    <span className={`text-base font-black transition-colors ${isChecked ? 'text-yellow-400' : 'text-yellow-400 group-hover:text-yellow-300'}`}>
                      {item.ticker}
                    </span>
                  </div>

                  <div className="px-2 py-0.5 bg-green-500/10 border border-green-500/20 rounded-full">
                    <span className="text-[10px] font-black text-green-400">
                      {item.count} Request{item.count > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="w-full text-left border-t border-white/5 pt-2 mt-1">
                  <p className="text-[9px] text-gray-500 uppercase font-bold tracking-tight">Last Requested:</p>
                  <p className="text-[10px] font-medium text-gray-400 truncate">
                    {item.lastRequest ? formatThaiDate(item.lastRequest) : 'ไม่มีข้อมูลเวลา'}
                  </p>
                </div>
              </Component>
            );
          })}

          {filteredStats.length === 0 && (
            <div className="col-span-full text-center py-10 text-gray-500 text-sm italic">ไม่พบข้อมูลคำขอวิเคราะห์</div>
          )}
        </div>

        {/* Footer Action Buttons */}
        <div className="p-4 border-t border-gray-800 bg-black/30 flex gap-3">
          {/* 1. ปุ่ม "เลือก" อยู่ซ้ายมือ */}
          <button
            onClick={toggleSelectMode}
            className={`px-6 py-3.5 rounded-xl text-sm font-black transition-all flex items-center justify-center gap-2 min-w-[120px] ${isSelectMode
                ? 'bg-red-500/10 border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white'
                : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
          >
            {isSelectMode ? '✕ ยกเลิก' : '📝 เลือก'}
          </button>

          {/* 2. ปุ่มเปิดแท็บทั้งหมด/เปิดเฉพาะที่เลือก อยู่ขวามือ */}
          <button
            onClick={handleOpenSelected}
            disabled={!isSelectMode || selectedTickers.length === 0}
            className="flex-1 Elect py-3.5 bg-green-500/10 border border-green-500/40 text-green-400 rounded-xl font-black text-sm hover:bg-green-500 hover:text-black transition-all shadow-[0_0_20px_rgba(34,197,94,0.1)] disabled:opacity-20 disabled:cursor-not-allowed disabled:border-gray-800 disabled:text-gray-500 disabled:shadow-none"
          >
            🚀 {isSelectMode
              ? `เปิดแท็บที่เลือกไว้ด้านหลัง (${selectedTickers.length} ตัว)`
              : 'โปรดเปิดโหมด "เลือกเอง" เพื่อดำเนินการเปิดแท็บ'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ แก้ Error TS1192: ต้องมี default export
export default RequestBoxPopup;